import { useContext, useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import Api from "Vendor/Api";
import { UserContext } from "Vendor/Context/UserContextProvider";

import LoadingButton from "Components/Button/LoadingButton";
import Collection from "Config/Collection";
import Property from "Entity/Property";
import GroupedAction from "Entity/Collection/ListingSetting/GroupedAction";

interface DuplicateModalProps {
    actionModalFormIsLoading: boolean
    collection?: Collection
    fromMain: boolean
    handleActionModalOnResponse: () => void
    handleActionModalOnSubmit: () => void
    handleActionModalOnSuccess: () => void
    handleShowActionModal: (
        groupedAction?: GroupedAction,
        actionFromMain?: boolean,
    ) => void
    handleShowMainActionModal: () => void
    selected: number[]
    showActionModals: { [key: string]: boolean | Property }
}


const _DuplicateModal: React.FC<DuplicateModalProps> = ({
    actionModalFormIsLoading,
    collection,
    fromMain,
    handleActionModalOnResponse,
    handleActionModalOnSubmit,
    handleActionModalOnSuccess,
    handleShowActionModal,
    handleShowMainActionModal,
    selected,
    showActionModals,
}) => {
    const { t } = useTranslation();
    const { currentUser } = useContext(UserContext);
    const translationKeys = currentUser?.currentOrganization?.languages || [];

    const [translations, setTranslations] = useState<{ [locale: string]: string }>({});
    const [baseName, setBaseName] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [fileNameError, setFileNameError] = useState<string | null>(null);

    const isProduct = collection === 'catalog';
    const isFile = collection === 'bucket';

    useEffect(() => {
        if (showActionModals.duplicate && collection && selected[0]) {
            setLoading(true);
            setFileNameError(null);

            Api.get(`/grouped-action/${collection}/${selected[0]}/duplicate`)
                .then((response) => {
                    if (isProduct) {
                        const rawData: Record<string, { name: string }> = response.data.suggestedName;
                        const extracted = Object.fromEntries(
                            Object.entries(rawData).map(([locale, obj]) => [locale, obj.name])
                        );
                        setTranslations(extracted);
                    } else if (isFile) {
                        setBaseName(response.data.suggestedName);
                    }
                })
                .catch(() => { })
                .finally(() => setLoading(false));
        }
    }, [showActionModals.duplicate, selected, collection]);

    const handleProductNameChange = (locale: string, value: string) => {
        setTranslations((prev) => ({ ...prev, [locale]: value }));
    };

    const handleFileNameChange = (value: string) => {
        setBaseName(value);
    };

    const duplicate = () => {
        handleActionModalOnSubmit();

        const url = `/grouped-action/${collection}/${selected[0]}/duplicate`;
        const payload = isProduct
            ? {
                translations: Object.entries(translations).map(([locale, translation]) => ({
                    locale,
                    translation,
                })),
            }
            : {
                name: baseName,
            };

        Api.post(url, payload)
            .then(() => {
                handleActionModalOnSuccess();
            })
            .catch((error) => {
                if (error.response?.status === 409) {
                    const message = error.response.data.error || "Something went wrong";
                    setFileNameError(message);
                } else {
                    setFileNameError("Something went wrong");
                }
            })
            .finally(() => handleActionModalOnResponse());
    };

    return (
        <Modal show={showActionModals.duplicate !== false} onHide={handleShowActionModal}>
            <Modal.Header closeButton={!fromMain}>
                <div className="modal-block-header">
                    {fromMain && (
                        <i
                            className="fa-solid fa-angle-left"
                            onClick={handleShowMainActionModal}
                        ></i>
                    )}
                    <Modal.Title>{t("app.duplicate")}</Modal.Title>
                </div>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center">
                        <Spinner animation="border" />
                    </div>
                ) : isProduct ? (
                    <div className="d-flex flex-column">
                        {translationKeys.map((locale) => (
                            <div className="form-floating mb-3" key={locale}>
                                <input
                                    id={`product-${locale}`}
                                    className="form-control"
                                    placeholder={`Name (${locale})`}
                                    value={translations[locale] || ""}
                                    onChange={(e) => handleProductNameChange(locale, e.target.value)}
                                />
                                <label htmlFor={`product-${locale}`}>
                                    {`${t("app.name")} (${locale})`}
                                </label>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="form-floating">
                        <input
                            className={`form-control ${fileNameError ? 'is-invalid' : ''}`}
                            placeholder={t("app.name")}
                            value={baseName}
                            onChange={(e) => {
                                handleFileNameChange(e.target.value)
                            }}
                        />
                        <label>{t("app.name")}</label>
                        {
                            <div className="invalid-feedback">
                                {fileNameError}
                            </div>
                        }
                    </div>

                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => {
                    handleShowActionModal()
                    setFileNameError(null)
                }
                }>
                    {t('app.cancel')}
                </Button>
                <LoadingButton
                    isLoading={actionModalFormIsLoading}
                    variant="primary"
                    onClick={duplicate}
                    disabled={loading || selected.length > 1}
                >
                    {t("app.duplicate")}
                </LoadingButton>
            </Modal.Footer>
        </Modal>
    );
};

export default _DuplicateModal;
